import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { Box, HStack, IconButton, Tooltip, useDisclosure, Button } from "@chakra-ui/react";
import { AgGridReact } from 'ag-grid-react'; // React Data Grid Component
import { ColDef, GetContextMenuItemsParams, IMultiFilterParams, IRowNode, MenuItemDef } from "ag-grid-enterprise";
import "ag-grid-community/styles/ag-grid.css"; // Mandatory CSS required by the Data Grid
import "ag-grid-community/styles/ag-theme-quartz.css"; // Optional Theme applied to the Data Grid
import download from 'downloadjs';
import "../../styles/grid-styles.css";
import { NotificationContext } from '../../components/NotificationPane';
import { FileType, NotificationContextType } from "../../types";
import { dateValueFormatter, dateValueFormatter_iso, dateTimeValueFormatter_iso, timezoneIndependantDateComparator, buildDefaultColDef, EDB_CUSTOM_withHeaderTooltips } from "../../util/AgGridFunctions";
import { useApi } from "../../hooks/useApi";
import { useColumnDefaults } from "../../hooks/useColumnDefaults";
import { FaArrowRotateLeft, FaArrowsRotate, FaFilter, FaDownload, FaEye, FaPencil } from 'react-icons/fa6';
import BaseTemplate from "../../components/BaseTemplate";
import PreFilter from "../../components/PreFilter";
import { fileTypeFromBuffer } from 'file-type';
import { v4 as uuidv4 } from 'uuid';
import { destructureDocPath } from "../../util/docPath";
import GridHeaderTooltip from "../../components/PersistentGrid/GridHeaderTooltip";

const DEFAULT_SHOW_DAYS = "30";

function FilesComponent() {
  const [loading, setLoading] = useState(true)
  const [fileData, setFileData] = useState<FileType[]>([])
  const { pushNotification } = useContext<NotificationContextType | null>(NotificationContext)!;
  const { getFiles, downloadFile, getDocByHash } = useApi();
  const columnDefaults = useColumnDefaults();
  const [gridKey, setGridKey] = useState<string>();
  const [showDays, setShowDays] = useState<string>(DEFAULT_SHOW_DAYS);
  useDisclosure();

  useEffect(() => {
    setGridKey(uuidv4())
  }, []);



  useEffect(() => {
    setLoading(true);
    loadFiles(DEFAULT_SHOW_DAYS)
      .catch(() => {
        pushNotification("Error loading files", "error");
      });
  }, [])

  async function loadFiles(days: string = showDays) {
    try {
      const response = await getFiles(days);
      await setLoading(false);
      setFileData(response);
    } catch (err) {
      console.error("Error fetching files");
    }
  }

  // The file's location comes from doc.file_store_v by filehash (the same lookup the Upload Log and
  // the preview page use); downloadAs is the row's EB filename, kept as the saved name.
  async function startDownload(filehash: string, downloadAs?: string) {
    let file = filehash;
    try {
      const doc = await getDocByHash(filehash);
      if (!doc) {
        pushNotification("This file is not in the hash library", "warning");
        return;
      }
      const { drive, dir, filename } = destructureDocPath(doc.doc_path);
      file = filename;
      const byteArray = await downloadFile(drive, dir, file);
      const detected = await fileTypeFromBuffer(byteArray);

      const blob = new Blob([byteArray], { type: detected?.mime });

      // Prefer EB filename; fall back to original filename
      const finalName = (downloadAs && downloadAs.trim().length > 0) ? downloadAs.trim() : file;

      download(blob, finalName);

      pushNotification(`File ${finalName} downloaded successfully`, "success");
    } catch (err) {
      pushNotification("Error downloading file", "error");
    } finally {
      setLoading(false);
    }
  }


  function queueAllSkForViewer() {
    if (!gridRef.current) return;

    const ids: string[] = [];

    gridRef.current.api.forEachNodeAfterFilterAndSort((node) => {
      if (node.data?.sid) ids.push(node.data.sid);
    });

    localStorage.setItem("viewer-queue", JSON.stringify(ids));
  }

  const gridRef = useRef<AgGridReact<FileType>>(null);

  // Column Definitions: Defines the columns to be displayed. useMemo (not useState) because the
  // 'date' spreads below depend on columnDefaults, which arrives asynchronously after login --
  // a useState initializer would have run once with the not-yet-loaded empty defaults and never
  // re-run when the DB values came in.
  const colDefs = useMemo<ColDef<FileType>[]>(() => EDB_CUSTOM_withHeaderTooltips([
    { field: "scantime", headerName: "Scan Time", ...buildDefaultColDef(columnDefaults, "scantime"), width: 140, sort: 'desc',valueFormatter: dateTimeValueFormatter_iso },
    { field: "doc_status", headerName: "Status", ...buildDefaultColDef(columnDefaults, "doc_status"), width: 100},
    { field: "doc_ref", headerName: "Reference", ...buildDefaultColDef(columnDefaults, "doc_ref"), width: 140},
    { field: "sid", headerName: "SID", ...buildDefaultColDef(columnDefaults, "sid"), hide: true },
    { field: "doc_from", headerName: "Source", ...buildDefaultColDef(columnDefaults, "doc_from"), width: 60 },
    { field: "doc_family", headerName: "Family", ...buildDefaultColDef(columnDefaults, "doc_family") },
    { field: "doc_month", headerName: "Month", ...buildDefaultColDef(columnDefaults, "doc_month") },
    { field: "doc_date", headerName: "Document Date", ...buildDefaultColDef(columnDefaults, "doc_date"), width: 105, cellDataType: 'dateString', filter: 'agDateColumnFilter', filterParams: { comparator: timezoneIndependantDateComparator }, valueFormatter: dateValueFormatter_iso },
    { field: "doc_period_from", headerName: "Period From", ...buildDefaultColDef(columnDefaults, "doc_period_from"), width: 105, cellDataType: 'dateString', filter: 'agDateColumnFilter', filterParams: { comparator: timezoneIndependantDateComparator }, valueFormatter: dateValueFormatter_iso },
    { field: "doc_period_to", headerName: "Period To", ...buildDefaultColDef(columnDefaults, "doc_period_to"), width: 105,cellDataType: 'dateString', filter: 'agDateColumnFilter', filterParams: { comparator: timezoneIndependantDateComparator }, valueFormatter: dateValueFormatter_iso },
    { field: "doc_total_amt_val", headerName: "Total Amount", ...buildDefaultColDef(columnDefaults, "doc_total_amt_val") },
    { field: "account_ref_scan", headerName: "Account Ref Scan", ...buildDefaultColDef(columnDefaults, "account_ref_scan"), hide: true},
    { field: "account_name", headerName: "Account Name", ...buildDefaultColDef(columnDefaults, "account_name"), hide: true },
    { field: "address_scan", headerName: "Address Scan", ...buildDefaultColDef(columnDefaults, "address_scan"), hide: true },
    { field: "file_modified", headerName: "File Modified", ...buildDefaultColDef(columnDefaults, "file_modified"), hide: true, cellDataType: 'dateString', filter: 'agDateColumnFilter', filterParams: { comparator: timezoneIndependantDateComparator }, valueFormatter: dateValueFormatter },
    { field: "doc_related_ref", headerName: "Related Doc Ref", ...buildDefaultColDef(columnDefaults, "doc_related_ref"), hide: true },
    { field: "filehash", headerName: "File Hash", ...buildDefaultColDef(columnDefaults, "filehash") },
    { field: "tag", headerName: "Tag", ...buildDefaultColDef(columnDefaults, "tag"), hide: true },
    { field: "scan_filename", headerName: "Scan Filename", ...buildDefaultColDef(columnDefaults, "scan_filename"), width: 350 },
    { field: "eb_filename", headerName: "EB Filename", ...buildDefaultColDef(columnDefaults, "eb_filename"), width: 350 },
    {
      cellRenderer: (params: any) => {
        return (
          <>
            <Tooltip label="Download" hasArrow>
              <IconButton
                icon={<FaDownload />}
                size="sm"
                onClick={() => {
                  setLoading(true);

                  const ebName = params.data?.eb_filename; // <-- use this as download name
                  startDownload(params.data!.filehash, ebName);
                }}
                aria-label={"Download"}
              >
                Download
              </IconButton>
            </Tooltip>
            <Tooltip label="Preview" hasArrow>
              <IconButton
                icon={<FaEye />}
                size="sm"
                onClick={() => {
                  window.open(`/preview/${params.data.filehash}`, "_blank", "noopener,noreferrer");
                }}
                aria-label={"Preview"}
              >
                Preview
              </IconButton>
            </Tooltip>
            <Tooltip label="Edit metadata" hasArrow>
              <IconButton
                icon={<FaPencil />}
                size="sm"
                onClick={() => {
                  window.open(`/file_viewer/${params.data.sid}`, "_blank", "noopener,noreferrer");
                }}
                aria-label={"Edit metadata"}
              >
                Edit
              </IconButton>
            </Tooltip>
          </>
        )
      },
      headerName: "Actions",
      headerTooltip: "Actions", // no menu on this column (suppressMenu below) -- override, not the default "- right-click for options" suffix
      width: 140,
      sortable: false,
      filter: false,
      suppressMenu: true,
      pinned: "left", // optional pin to right
      cellStyle: { alignItems: "center", justifyContent: "center" }
    },
  ]), [columnDefaults]);

  const defaultColDef = useMemo<ColDef>(() => {
    return {
      ...buildDefaultColDef(columnDefaults),
      floatingFilter: true,
      enableRowGroup: true,
      width: 120,
      filter: 'agMultiColumnFilter',
      filterParams: {
        buttons: ["clear", "apply"],
        suppressRemoveEntries: true,
      } as IMultiFilterParams,
      tooltipComponent: GridHeaderTooltip,
      valueFormatter: (p) => {
        const v = p.value;

        if (
          v == null ||                 // null or undefined
          v === "null" ||              // string "null"
          v === "undefined"            // string "undefined" (optional but recommended)
        ) {
          return "";
        }

        return String(v);
      },
    };
  }, [columnDefaults]);

  const statusBar = useMemo(() => {
    return {
      statusPanels: [
        { statusPanel: 'agTotalAndFilteredRowCountComponent' },
      ]
    };
  }, []);

  const clearFilters = () => {
    if (!gridRef.current) return;
    gridRef.current.api.setFilterModel(null);
    gridRef.current.api.onFilterChanged();
  };

  const reloadAll = async () => {
    setLoading(true);
    await loadFiles(showDays);
    clearFilters();
  };

  const reloadFiltered = async () => {
    const currentFilterModel = gridRef.current?.api.getFilterModel() ?? null;
    setLoading(true);
    await loadFiles(showDays);
    if (gridRef.current) {
      gridRef.current.api.setFilterModel(currentFilterModel);
      gridRef.current.api.onFilterChanged();
    }
  };

  // PreFilter's Run button - the day-range param actually changes the DB query (doc.sp_library_get's
  // @p_show_days), unlike reloadAll/reloadFiltered above which just re-fetch the same range - so
  // this always clears filters rather than trying to preserve a filter model against a
  // fundamentally different result set.
  const handleRunPreFilter = async () => {
    setLoading(true);
    await loadFiles(showDays);
    clearFilters();
  };

  // Filter-in/out via the right-click context menu, layered as an independent AG Grid "external
  // filter" (isExternalFilterPresent/doesExternalFilterPass) rather than written into each
  // column's own native filter model. A per-column value-include/exclude set works no matter
  // what native filter type that column happens to be configured with (agTextColumnFilter,
  // agSetColumnFilter, agMultiColumnFilter, or a gui.column_default DB-driven override of any of
  // those) - the external filter runs as an extra AND'd condition on top, so it never has to
  // guess at (or fight) the shape of the column's own filter model. AG Grid combines external
  // filter results with each column's native filter automatically.
  const columnValueFiltersRef = useRef<Record<string, { mode: 'in' | 'out'; values: Set<unknown> }>>({});

  const isExternalFilterPresent = useCallback(
    () => Object.keys(columnValueFiltersRef.current).length > 0,
    [],
  );

  const doesExternalFilterPass = useCallback((node: IRowNode<FileType>) => {
    for (const [colId, f] of Object.entries(columnValueFiltersRef.current)) {
      const cellValue = (node.data as any)?.[colId];
      if (f.mode === 'in' && !f.values.has(cellValue)) return false;
      if (f.mode === 'out' && f.values.has(cellValue)) return false;
    }
    return true;
  }, []);

  const getContextMenuItems = useCallback(
    (p: GetContextMenuItemsParams): (string | MenuItemDef)[] => {
      const { value, column, api, defaultItems: agDefaults } = p;
      if (value == null) return agDefaults ?? [];
      if (column == null) return agDefaults ?? [];

      const colId = column.getColId();
      const defaults: (string | MenuItemDef)[] = agDefaults ?? [];
      const columnLabel = column.getColDef().headerName ?? colId;

      const filterIn: MenuItemDef = {
        name: `Filter ${columnLabel} - include "${value}"`,
        action: () => {
          columnValueFiltersRef.current = {
            ...columnValueFiltersRef.current,
            [colId]: { mode: 'in', values: new Set([value]) },
          };
          api.onFilterChanged();
        },
      };

      const filterOut: MenuItemDef = {
        name: `Filter ${columnLabel} - exclude "${value}"`,
        action: () => {
          const prev = columnValueFiltersRef.current[colId];
          const values = prev?.mode === 'out' ? new Set(prev.values) : new Set<unknown>();
          values.add(value);
          columnValueFiltersRef.current = { ...columnValueFiltersRef.current, [colId]: { mode: 'out', values } };
          api.onFilterChanged();
        },
      };

      const filterColumnReset: MenuItemDef = {
        name: `Filter ${columnLabel} - clear`,
        action: async () => {
          const next = { ...columnValueFiltersRef.current };
          delete next[colId];
          columnValueFiltersRef.current = next;
          await api.setColumnFilterModel(colId, null);
          api.onFilterChanged();
        },
      };

      const filterClearAll: MenuItemDef = {
        name: 'Filter - Reset All',
        action: () => {
          columnValueFiltersRef.current = {};
          api.setFilterModel(null);
          api.onFilterChanged();
        },
      };

      return [filterIn, filterOut, filterColumnReset, filterClearAll, 'separator', ...defaults];
    },
    [],
  );

  //if (fileData === undefined || loading) return <Spinner />
  return (
    <>
      <BaseTemplate fitToWindow={true}>
        <BaseTemplate.Header>
          <HStack>
            <Tooltip label="Reload All (clear filters)" hasArrow>
              <IconButton
                icon={<FaArrowsRotate />}
                onClick={reloadAll}
                aria-label="Reload All"
                variant="ghost"
              />
            </Tooltip>
            <Tooltip label="Reload Filtered (keep filters)" hasArrow>
              <IconButton
                icon={<FaArrowRotateLeft />}
                onClick={reloadFiltered}
                aria-label="Reload Filtered"
                variant="ghost"
              />
            </Tooltip>
            <Tooltip label="Clear Filters" hasArrow>
              <IconButton
                icon={<FaFilter />}
                onClick={clearFilters}
                aria-label="Clear Filters"
                variant="ghost"
              />
            </Tooltip>
            <Button
              size="sm"
              variant="outline"
              onClick={() => gridRef.current?.api.selectAll()}
            >
              Select All
            </Button>
          </HStack>
        </BaseTemplate.Header>
        <BaseTemplate.Body>
          <Box display="flex" flexDirection="column" h="100%" gap={2}>
            <PreFilter
              fields={[{
                name: "show_days",
                label: "Show Days",
                width: "70px",
                options: [
                  { label: "30", value: "30" },
                  { label: "90", value: "90" },
                  { label: "ALL", value: "" },
                ],
              }]}
              values={{ show_days: showDays }}
              onChange={(_name, value) => setShowDays(value)}
              onRun={handleRunPreFilter}
              running={loading}
            />
            <Box flex="1" minH={0}>
              <div className="ag-theme-quartz" style={{ width: "100%", height: "100%" }} >
                <AgGridReact
                  key={gridKey}
                  getContextMenuItems={getContextMenuItems}
                  isExternalFilterPresent={isExternalFilterPresent}
                  doesExternalFilterPass={doesExternalFilterPass}
                  statusBar={statusBar}
                  tooltipShowDelay={200}
                  loading={loading}
                  ref={gridRef}
                  rowData={fileData}
                  columnDefs={colDefs}
                  defaultColDef={defaultColDef}
                  groupDisplayType={"groupRows"}
                  rowGroupPanelShow={"always"}
                  rowSelection="multiple"
                  rowMultiSelectWithClick
                  onModelUpdated={queueAllSkForViewer}
                  headerHeight={32}
                  rowHeight={32}
                  floatingFiltersHeight={32}
                />
              </div>
            </Box>
          </Box>
        </BaseTemplate.Body>
      </BaseTemplate>
    </>
  )

}

export default FilesComponent
